import Results from './results'
import toSnakeCase from '../../../util/toSnakeCase'
import PresenterPoll from '../presenterPoll'
import { transportEvents, gql } from '@polleverywhere/graphql-client'
import SubModel from '@polleverywhere/core/lib/models/mixins/_sub_model'
import FlashOptions from '@polleverywhere/core/lib/models/flash_options'
import { createAllSubscriptions } from '../../presenter/discourse/index.subscriptions'

const presentersDiscourseQuery = gql`
  query presentersDiscourse($presenterKey: ID, $sharedKey: ID) {
    presentersDiscourse(presenterKey: $presenterKey, sharedKey: $sharedKey) {
      pollId
      presenterKey
      presenterSharedKey
      sharedKey
      participantKey
      accountId
      title
      type
      featuredResultId
      activated
      anonymous
      simpleKeywords
      sessionKeyword
      shortcode
      smsEnabled
      webEnabled
      showParticipants
      resultsCount
      state
      displayState
      countdownSeconds
      showRecent
      moderationEnabled
      autocensor
      emojiFilter
      sharedModeration
      legacyVisualSettings
      groupId
      position
      presenter {
        id
        login
      }
      results {
        id
        value
        upvotes
        downvotes
        netVotes
        participantIdentifier
        approved
        createdAt
      }
    }
  }
`

const featureDiscourseResultMutation = gql`
  mutation featureDiscourseResult($input: FeatureResultInput!) {
    featureDiscourseResult(input: $input) {
      featuredResultId
    }
  }
`

const MUTATABLE_PROPS = {
  state: { key: 'state', val: model => model.get('state').toUpperCase() },
  display_state: { key: 'displayState', val: model => model.get('display_state').toUpperCase() },
  show_recent: { key: 'showRecent', val: model => !!model.get('show_recent') },
  moderation_enabled: { key: 'moderationEnabled', val: model => !!model.get('moderation_enabled') },
  shared_moderation: { key: 'sharedModeration', val: model => model.get('shared_moderation').toUpperCase() },
  autocensor: { key: 'autocensor', val: model => model.get('autocensor') ? model.get('autocensor').toUpperCase() : null },
  emoji_filter: { key: 'emojiFilter', val: model => !!model.get('emoji_filter') },
  title: { key: 'title', val: model => model.get('title') },
  title_hidden: { key: 'titleHidden', val: model => !!model.get('title_hidden') },
  anonymous: { key: 'anonymous', val: model => !!model.get('anonymous') },
  registered_participants_only: { key: 'registeredParticipantsOnly', val: model => !!model.get('registered_participants_only') },
  max_votes: { key: 'maxVotes', val: model => model.get('max_votes') }
}

function buildDiscourseMutation ({ onlyChanged }) {
  const changedKeys = this.changed && Object.keys(this.changed)
  const propsToSync = onlyChanged && changedKeys.length ? changedKeys : Object.keys(MUTATABLE_PROPS)
  const updateKeys = []
  const input = { presenterKey: this.get('presenter_key') }

  propsToSync.forEach(prop => {
    if (prop in MUTATABLE_PROPS) {
      const def = MUTATABLE_PROPS[prop]
      updateKeys.push(def.key)
      input[def.key] = def.val(this)
    } else {
      throw new Error(`Cannot change Q&A property: ${prop}`)
    }
  })

  return {
    action: 'mutate',
    parse: (json) => toSnakeCase(json.updateDiscourse),
    query: gql`
      mutation updateDiscourse($input: DiscourseInput!) {
        updateDiscourse(input: $input) {
          ${updateKeys.join('\n')}
        }
      }
    `,
    variables: { input }
  }
}

export default class Discourse extends PresenterPoll {
  get idAttribute () {
    return 'poll_id'
  }

  type () {
    return 'discourse'
  }

  // These models combine the shared and presenter properties, so the 'poll' is the 'instance'
  // FlashOptions looks to `this.poll.xyz` for some data
  get poll () {
    return this
  }

  getQuery (action) {
    switch (action) {
      case 'read':
        return {
          action: 'query',
          parse: (json) => toSnakeCase(json.presentersDiscourse),
          query: presentersDiscourseQuery,
          variables: {
            presenterKey: this.get('presenter_key'),
            sharedKey: this.get('shared_key') || this.get('permalink')
          }
        }
      case 'update':
        return buildDiscourseMutation.call(this, { onlyChanged: false })
      case 'patch':
        return buildDiscourseMutation.call(this, { onlyChanged: true })
      case 'feature':
        return {
          action: 'mutate',
          parse: (json) => toSnakeCase(json.featureDiscourseResult),
          query: featureDiscourseResultMutation,
          variables: {
            input: {
              presenterKey: this.get('presenter_key'),
              resultId: this.get('featured_result_id')
            }
          }
        }
      default:
        throw new Error(`Unknown query for ${action}`)
    }
  }

  flashOptions () {
    return this._flashOptions
  }

  supportsModeration () {
    return true
  }

  /**
   * Collection of submitted question results. This overrides the
   * base property to make it return a discourse results collection instead.
   * @return {Results}
   */
  get results () {
    return this._results
  }

  initialize (attrs = {}) {
    super.initialize(attrs)

    if (!attrs.presenter_key && !attrs.shared_key && !attrs.permalink) {
      throw new TypeError('presenter_key, presenter_key, or permalink must be provided')
    }

    this.include(SubModel)
    this.initSubModel('presenter')
    this.initSubModel('flash_options', {
      model: FlashOptions,
      prefix: '_'
    }).poll = this

    this._results = new Results(this.get('results'), { poll: this })

    // We refresh all the data when the subscription transport reconnects
    // as Absinthe/Phoenix doesn't resume dead subscriptions, it creates
    // new ones if you've been fully disconnected (it can resume if briefly
    // disconnected though)
    // https://github.com/polleverywhere/conduit/issues/9
    this._gqlSubscriptionOnlineHandler = (data) => {
      if (data.reconnect) {
        this.fetch()
      }
    }

    // Viz seems to look for this attribute
    this.set('type', 'discourse')
  }

  parse (data) {
    // All results are included in the instance API and parsed into the results
    // collection. Json key is removed so we maintain a single source of truth.
    // Use `set` to trigger events for all actions: add, update, remove.
    if (data.results) {
      this.results.set(data.results, { parse: true })
      delete data.results
    }

    // The GraphQL API uses the 'legacy_visual_settings' key to store visual settings
    // as it's planned to have this be structured, and typed, data in the 'near' future
    // If the server sends a `null` value, then none of these settings have
    // changed — it doesn't mean to set `flashOptions` to `null`.
    if (data.legacy_visual_settings) {
      this.flashOptions().set(data.legacy_visual_settings, { parse: true })
      delete data.legacy_visual_settings
    }

    // GraphQL types these as uppercase constants
    if (data.type) data.type = data.type.toLowerCase()
    if (data.state) data.state = data.state.toLowerCase()
    if (data.display_state) data.display_state = data.display_state.toLowerCase()
    if (data.autocensor) data.autocensor = data.autocensor.toLowerCase()

    // Map to legacy property
    if (typeof data.activated !== 'undefined') data.current = data.activated

    // Convert graphql ID types (strings) into integers
    if (data.account_id) data.account_id = parseInt(data.account_id, 10)
    if (data.presenter?.id) data.presenter.id = parseInt(data.presenter.id, 10)
    if (data.group_id) data.group_id = parseInt(data.group_id, 10)

    return data
  }

  isActivated () {
    return this.get('current')
  }

  featureResult (resultId = null) {
    this.save({ featured_result_id: resultId }, { patch: true, method: 'feature' })
  }

  /**
   * Live update the discourse data
   * Not using instance.start as this doesn't use firehose
   */
  liveUpdate () {
    this._activeSubscriptions = createAllSubscriptions(this)
    transportEvents.on('connected', this._gqlSubscriptionOnlineHandler)
  }

  stopLiveUpdate () {
    if (this._activeSubscriptions) {
      this._activeSubscriptions.forEach(subscriptionPromise => {
        subscriptionPromise.then(s => s.unsubscribe()).catch(() => {})
      })
      this._activeSubscriptions = []
      transportEvents.off('connected', this._gqlSubscriptionOnlineHandler)
    }
  }
}
